import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/cn";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "ghost" | "danger" | "subtle";
export type ButtonSize = "sm" | "md" | "lg";

const base =
  "relative inline-flex select-none items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-[transform,box-shadow,background-color,border-color,opacity] duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-flamingo disabled:pointer-events-none disabled:opacity-50 active:translate-y-px";

const variants: Record<ButtonVariant, string> = {
  primary:
    "text-white bg-linear-to-br from-flamingo-darker to-magenta-dark shadow-glow-sm hover:-translate-y-0.5 hover:shadow-glow active:translate-y-0",
  ghost:
    "text-white/85 border border-white/12 bg-white/[0.03] backdrop-blur-md hover:bg-white/[0.07] hover:border-white/20 hover:text-white",
  subtle:
    "text-flamingo-lighter bg-flamingo/12 hover:bg-flamingo/18",
  danger:
    "text-white bg-danger/90 hover:bg-danger shadow-[0_10px_30px_-12px_rgba(255,92,108,0.6)] hover:-translate-y-0.5",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-6 text-md",
  lg: "h-13 px-8 text-base",
};

export function buttonClasses(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  fullWidth = false,
  className?: string,
) {
  return cn(base, variants[variant], sizes[size], fullWidth && "w-full", className);
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const t = useTranslations("common");
  return (
    <button
      className={buttonClasses(variant, size, fullWidth, className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading && (
        <Spinner
          size={size === "lg" ? "md" : "sm"}
          className="absolute"
          label={t("working")}
        />
      )}
      <span
        className={cn(
          "inline-flex items-center gap-2",
          isLoading && "opacity-0",
        )}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    </button>
  );
}

export interface LinkButtonProps
  extends Omit<React.ComponentProps<typeof Link>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}

/** Same styling as Button, rendered as a Next.js Link for navigation. */
export function LinkButton({
  variant = "primary",
  size = "md",
  fullWidth = false,
  className,
  children,
  ...props
}: LinkButtonProps) {
  return (
    <Link className={buttonClasses(variant, size, fullWidth, className)} {...props}>
      {children}
    </Link>
  );
}
